import React, {useCallback, useMemo, useState} from 'react';

import {SettingLabel} from '../components/SettingLabel';
import {BucketCustomization, SemanticLegendSettings} from '../types';
import {BucketCustomizationInput} from './BucketCustomizationInput';
import {SemanticLegendBucketsActions} from './SemanticLegendBucketsActions';
import {SemanticLegendsPalette} from './SemanticLegendsPalette';

export type SemanticLegendBucketCustomizationProps = {
  buckets: BucketCustomization[];
  bucketCustomizations: Record<number, BucketCustomization>;
  updateSetting: (settings: Partial<SemanticLegendSettings>) => void;
};

export const SemanticLegendBuckets = ({
  buckets,
  bucketCustomizations,
  updateSetting,
}: SemanticLegendBucketCustomizationProps) => {
  // Create content-based keys that update when bucket values change
  const ids = useMemo(
    () =>
      buckets.map((bucket, i) => {
        const customization = bucketCustomizations[i];
        const minVal = (bucket.bucketMin ?? 0).toFixed(3);
        const maxVal = (bucket.bucketMax ?? 0).toFixed(3);
        const color = customization?.color || bucket.color || 'default';
        return `bucket-${i}-${minVal}-${maxVal}-${color}`;
      }),
    [buckets, bucketCustomizations]
  );

  const updateBucketCustomizationSetting = useCallback(
    (settings: Partial<BucketCustomization>, index: number) => {
      const newBucketCustomizations = {...bucketCustomizations};
      newBucketCustomizations[index] = {
        ...newBucketCustomizations[index],
        ...settings,
      };
      updateSetting({
        bucketCustomizations: newBucketCustomizations,
        buckets: buckets,
      });
    },
    [updateSetting, bucketCustomizations, buckets]
  );

  // Create stable callback functions for each bucket
  const bucketCallbacks = useMemo(() => {
    return buckets.map(
      (_, index) => (settings: Partial<BucketCustomization>) =>
        updateBucketCustomizationSetting(settings, index)
    );
  }, [buckets, updateBucketCustomizationSetting]);

  const bucketCustomizationList = useMemo(() => {
    return buckets?.map((bucket, index) => (
      <li key={ids[index]}>
        <BucketCustomizationInput
          bucket={bucket}
          bucketCustomization={bucketCustomizations[index] ?? {}}
          index={index}
          numberOfBuckets={buckets?.length}
          updateBucketSetting={bucketCallbacks[index]}
        />
      </li>
    ));
  }, [buckets, bucketCustomizations, ids, bucketCallbacks]);

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex flex-col gap-8">
      <div className="flex w-full gap-8">
        <SettingLabel text="Buckets" className="flex w-full" />
        <SemanticLegendsPalette />
        <SemanticLegendBucketsActions setIsOpen={setIsOpen} isOpen={isOpen} />
      </div>
      <ul className="flex flex-col gap-8">{bucketCustomizationList}</ul>
    </div>
  );
};
